import { Fragment, useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import {
  Paper,
  TextField,
  Grid,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  makeStyles,
} from '@material-ui/core';
import { axiosGetRequest, axiosPatchRequest } from '../../../utils/axiosRequests';

moment.locale('fr');

function buildClosureSentence(start, end) {
  if (!start || !end) {
    return 'Aucune fermeture programmée';
  }
  if (start === end) {
    return `Les livraisons ne sont pas disponibles à la date du ${moment(start).format('LL')}`;
  }
  return `Les livraisons ne sont pas disponibles du ${moment(start).format('LL')} au ${moment(end).format(
    'LL'
  )} (inclus)`;
}

const useStyle = makeStyles({
  paper: {
    padding: '1rem',
    height: '100%',
    '& > h3': {
      marginTop: 0,
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
});

export default function CloseWebsite() {
  const classes = useStyle();
  const token = Cookies.get('jwt');
  const { enqueueSnackbar } = useSnackbar();

  const [savedClosure, setSavedClosure] = useState({ start: null, end: null });
  const [formClosure, setFormClosure] = useState(() => {
    const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
    return { start: tomorrow, end: tomorrow };
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const today = moment().format('YYYY-MM-DD');

  const validation = useMemo(() => {
    const { start, end } = formClosure;
    const isIncomplete = !start || !end;
    const isInverted = !!start && !!end && start > end;
    const isEndInPast = !!end && end < today;
    return {
      isIncomplete,
      isInverted,
      isEndInPast,
      isSubmitDisabled: isIncomplete || isInverted || isEndInPast,
      endHelperText: isInverted
        ? 'La date de fin doit être postérieure ou égale à la date de début'
        : isEndInPast
        ? "La date de fin doit être aujourd'hui ou plus tard"
        : '',
    };
  }, [formClosure, today]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormClosure((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const update = {
        closureTimes: {
          start: formClosure.start,
          end: formClosure.end,
        },
      };
      await axiosPatchRequest('/configs/closureTimes', token, update);
      setSavedClosure({ start: formClosure.start, end: formClosure.end });
      enqueueSnackbar('Dates de fermeture mises à jour', { variant: 'success' });
      setConfirmOpen(false);
    } catch (error) {
      enqueueSnackbar('Échec de la mise à jour. Réessayez.', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      const closureTimesData = await axiosGetRequest('/configs/closureTimes')
        .then((res) => res.data.data.infos)
        .catch((err) => console.log(err));

      if (closureTimesData?.setting?.closureTimes) {
        const { start, end } = closureTimesData.setting.closureTimes;
        setSavedClosure({ start: start ?? null, end: end ?? null });
      }
    };
    fetch();
  }, []);

  return (
    <Fragment>
      <h1>Fermeture du site</h1>

      <Box>
        <Grid container spacing={3}>
          <Grid item xs={7}>
            <Paper className={classes.paper}>
              <h3>Statut actuel</h3>
              <p>{buildClosureSentence(savedClosure.start, savedClosure.end)}</p>
            </Paper>
          </Grid>

          <Grid item xs={5}>
            <Paper className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="start"
                    type="date"
                    size="small"
                    variant="outlined"
                    label="Date de début"
                    InputLabelProps={{ shrink: true }}
                    value={formClosure.start}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="end"
                    type="date"
                    size="small"
                    variant="outlined"
                    label="Date de fin"
                    InputLabelProps={{ shrink: true }}
                    value={formClosure.end}
                    onChange={handleChange}
                    error={validation.isInverted || validation.isEndInPast}
                    helperText={validation.endHelperText}
                  />
                </Grid>
              </Grid>
              <Box className={classes.buttonRow}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={validation.isSubmitDisabled || isSubmitting}
                  onClick={() => setConfirmOpen(true)}
                >
                  Valider
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !isSubmitting && setConfirmOpen(false)}>
        <DialogTitle>Confirmer la fermeture du site</DialogTitle>
        <DialogContent>
          <DialogContentText>{buildClosureSentence(formClosure.start, formClosure.end)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" disabled={isSubmitting}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
